import type { Field, FieldAnswer, Section } from '@anuleaza/shared';
import { Input, TextArea } from '../primitives/Input.js';
import { MonoLabel } from '../primitives/MonoLabel.js';

interface ChestionarSectionProps {
  section: Section;
  sectionIndex: number;
  totalSections: number;
  /** Current answers, keyed by field.id. */
  answers: Record<string, FieldAnswer>;
  /** Pre-filled values from OCR, keyed by field.id (string only). */
  prefills: Record<string, string | null>;
  onChange: (fieldId: string, answer: FieldAnswer | null) => void;
}

export function ChestionarSection({
  section,
  sectionIndex,
  totalSections,
  answers,
  prefills,
  onChange,
}: ChestionarSectionProps) {
  const visibleFields = section.fields.filter((f) => isFieldVisible(f, answers));
  return (
    <div className="flex flex-col gap-7">
      <div className="flex items-center justify-between">
        <MonoLabel>{`Secțiunea ${sectionIndex + 1} din ${totalSections}`}</MonoLabel>
        <div className="flex gap-1.5">
          {Array.from({ length: totalSections }).map((_, i) => (
            <div
              key={i}
              className={[
                'h-1 w-6 rounded-full transition',
                i <= sectionIndex ? 'bg-fg' : 'bg-line',
              ].join(' ')}
            />
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-pretty mb-2">{section.title}</h2>
        {section.subtitle && (
          <p className="text-muted text-sm sm:text-base leading-relaxed">{section.subtitle}</p>
        )}
      </div>

      <div className="flex flex-col gap-7">
        {visibleFields.map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            answer={answers[field.id] ?? null}
            prefill={prefills[field.id] ?? null}
            onChange={(a) => onChange(field.id, a)}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================
//  Field-type renderers
// ============================================================

interface FieldRendererProps {
  field: Field;
  answer: FieldAnswer | null;
  prefill?: string | null;
  onChange: (a: FieldAnswer | null) => void;
}

function FieldRenderer({ field, answer, prefill, onChange }: FieldRendererProps) {
  return (
    <div className="flex flex-col gap-2.5">
      <div>
        <div className="text-[15px] font-medium leading-snug text-fg-2">
          {field.question}
          {field.required && <span className="ml-1 text-danger" aria-hidden>*</span>}
        </div>
        {field.hint && (
          <div className="text-[13px] text-muted mt-1 leading-snug">{field.hint}</div>
        )}
      </div>

      {field.type === 'radio' && (
        <RadioGroup field={field} answer={answer} onChange={onChange} />
      )}
      {field.type === 'multi_select' && (
        <MultiSelectGroup field={field} answer={answer} onChange={onChange} />
      )}
      {field.type === 'text' && (
        <TextInput field={field} answer={answer} prefill={prefill} onChange={onChange} />
      )}
      {field.type === 'textarea' && (
        <TextAreaInput field={field} answer={answer} onChange={onChange} />
      )}
    </div>
  );
}

function RadioGroup({ field, answer, onChange }: FieldRendererProps) {
  const selectedId = answer?.type === 'radio' ? answer.optionId : null;
  return (
    <div className="grid gap-2">
      {(field.options ?? []).map((opt) => {
        const selected = selectedId === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() =>
              onChange({
                type: 'radio',
                optionId: opt.id,
                label: opt.label,
                context: opt.context,
              })
            }
            className={[
              'group w-full text-left px-4 py-3 rounded-xl border transition flex items-start gap-3',
              selected
                ? 'border-fg bg-fg text-bg'
                : opt.hot
                  ? 'border-accent/40 bg-accent-soft/40 hover:border-accent hover:bg-accent-soft'
                  : 'border-line bg-card hover:border-fg-2',
            ].join(' ')}
          >
            <span
              className={[
                'mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0 transition',
                selected ? 'border-bg' : opt.hot ? 'border-accent' : 'border-line',
              ].join(' ')}
            >
              {selected && <span className="h-1.5 w-1.5 rounded-full bg-bg" />}
            </span>
            <span className="flex-1 text-[14px] leading-snug">
              {opt.label}
              {opt.hot && !selected && (
                <span className="ml-2 inline-block text-[10px] uppercase tracking-wider font-mono text-accent-dark border border-accent/40 rounded-sm px-1.5 py-0.5 align-middle">
                  apărare puternică
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MultiSelectGroup({ field, answer, onChange }: FieldRendererProps) {
  const selectedIds = new Set(answer?.type === 'multi' ? answer.optionIds : []);
  const toggle = (optId: string) => {
    const next = new Set(selectedIds);
    if (next.has(optId)) next.delete(optId);
    else next.add(optId);

    const options = field.options ?? [];
    const ids = [...next];
    if (ids.length === 0) {
      onChange(null);
      return;
    }
    onChange({
      type: 'multi',
      optionIds: ids,
      labels: ids.map((id) => options.find((o) => o.id === id)?.label ?? id),
      contexts: ids
        .map((id) => options.find((o) => o.id === id)?.context)
        .filter((c): c is string => typeof c === 'string'),
    });
  };
  return (
    <div className="grid gap-2">
      {(field.options ?? []).map((opt) => {
        const selected = selectedIds.has(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => toggle(opt.id)}
            className={[
              'w-full text-left px-4 py-3 rounded-xl border transition flex items-start gap-3',
              selected
                ? 'border-fg bg-card'
                : opt.hot
                  ? 'border-accent/40 bg-accent-soft/40 hover:border-accent'
                  : 'border-line bg-card hover:border-fg-2',
            ].join(' ')}
          >
            <span
              className={[
                'mt-0.5 h-4 w-4 rounded border-2 flex items-center justify-center shrink-0',
                selected
                  ? 'border-fg bg-fg text-bg'
                  : opt.hot
                    ? 'border-accent'
                    : 'border-line',
              ].join(' ')}
            >
              {selected && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 5l2.5 2.5L9 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span className="flex-1 text-[14px] leading-snug">
              {opt.label}
              {opt.hot && !selected && (
                <span className="ml-2 inline-block text-[10px] uppercase tracking-wider font-mono text-accent-dark border border-accent/40 rounded-sm px-1.5 py-0.5 align-middle">
                  important
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ field, answer, prefill, onChange }: FieldRendererProps) {
  const value = answer?.type === 'text' ? answer.value : prefill ?? '';
  return (
    <div>
      <Input
        value={value}
        placeholder={field.placeholder ?? ''}
        onChange={(e) => onChange({ type: 'text', value: e.target.value })}
      />
      {prefill && (answer == null || (answer.type === 'text' && answer.value === prefill)) && (
        <div className="text-[11px] text-accent-dark mt-1.5 font-mono uppercase tracking-wider">
          Pre-completat din OCR — corectează dacă e nevoie
        </div>
      )}
    </div>
  );
}

function TextAreaInput({ field, answer, onChange }: FieldRendererProps) {
  const value = answer?.type === 'text' ? answer.value : '';
  return (
    <TextArea
      value={value}
      placeholder={field.placeholder ?? ''}
      rows={4}
      onChange={(e) => onChange({ type: 'text', value: e.target.value })}
    />
  );
}

// ============================================================
//  Conditional visibility
// ============================================================

function isFieldVisible(field: Field, answers: Record<string, FieldAnswer>): boolean {
  if (!field.showWhen) return true;
  const dep = answers[field.showWhen.fieldId];
  if (!dep) return false;
  if (dep.type === 'radio') return dep.optionId === field.showWhen.equals;
  if (dep.type === 'multi') return dep.optionIds.includes(field.showWhen.equals);
  return false;
}

export function isSectionComplete(
  section: Section,
  answers: Record<string, FieldAnswer>,
): boolean {
  for (const f of section.fields) {
    if (!f.required) continue;
    if (!isFieldVisible(f, answers)) continue;
    const a = answers[f.id];
    if (!a) return false;
    if (a.type === 'radio' && !a.optionId) return false;
    if (a.type === 'multi' && a.optionIds.length === 0) return false;
    if (a.type === 'text' && !a.value.trim()) return false;
  }
  return true;
}
